import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useVariant } from '../../contexts/VariantContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getDomainImageAuto, handleImageErrorWithFallback } from '../../utils/imageManager';

const VariantImageSlider = () => {
  const { variant } = useVariant();
  const { brand, domainConfig } = useTheme();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const imageRefs = useRef([]);

        // Domain-specific image galleries
        const getDomainImages = () => {
          // Auto-detect gallery images for all domains
          const gallery1 = getDomainImageAuto(brand.id, 'gallery-1', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']);
          const gallery2 = getDomainImageAuto(brand.id, 'gallery-2', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']);
          const gallery3 = getDomainImageAuto(brand.id, 'gallery-3', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']);
          const gallery4 = getDomainImageAuto(brand.id, 'gallery-4', ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp']);

          return [
            {
              src: gallery1,
              alt: 'Gallery Image 1'
            },
            {
              src: gallery2,
              alt: 'Gallery Image 2'
            },
            {
              src: gallery3,
              alt: 'Gallery Image 3'
            },
            {
              src: gallery4,
              alt: 'Gallery Image 4'
            }
          ];
        };

  const images = getDomainImages();

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, images.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  // Variant-specific styling
  const getVariantClasses = () => {
    switch (variant) {
      case 'minimal':
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-200 shadow-sm',
          button: 'bg-gray-900 text-white hover:bg-gray-800',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600'
        };
      case 'modern':
        return {
          container: 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900',
          card: 'bg-white/5 backdrop-blur-md border border-white/10',
          button: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600',
          text: 'text-white',
          textSecondary: 'text-purple-100'
        };
      case 'corporate':
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-200 shadow-sm',
          button: 'bg-blue-600 text-white hover:bg-blue-700',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600'
        };
      case 'creative':
        return {
          container: 'bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100',
          card: 'bg-white/80 backdrop-blur-sm border border-white/50 shadow-lg',
          button: 'bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600',
          text: 'text-gray-900',
          textSecondary: 'text-gray-700'
        };
      case 'elegant':
        return {
          container: 'bg-gradient-to-br from-blue-50 via-white to-blue-100',
          card: 'bg-white/80 backdrop-blur-sm border border-blue-200/50 shadow-sm',
          button: 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800',
          text: 'text-blue-900',
          textSecondary: 'text-blue-600'
        };
      case 'dark':
        return {
          container: 'bg-gray-900',
          card: 'bg-white/5 backdrop-blur-md border border-white/10',
          button: 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700',
          text: 'text-white',
          textSecondary: 'text-gray-300'
        };
      default:
        return {
          container: 'bg-white',
          card: 'bg-white border border-gray-200 shadow-sm',
          button: 'bg-blue-600 text-white hover:bg-blue-700',
          text: 'text-gray-900',
          textSecondary: 'text-gray-600'
        };
    }
  };

  const variantClasses = getVariantClasses();

  return (
    <div className={`py-24 ${variantClasses.container}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className={`inline-block py-1 px-3 text-xs font-semibold rounded-full ${
            variant === 'modern' ? 'bg-white/10 text-white border border-white/20' :
            variant === 'dark' ? 'bg-white/10 text-white border border-white/20' :
            variant === 'creative' ? 'bg-white/20 text-gray-800 border border-white/30' :
            variant === 'elegant' ? 'bg-slate-100 text-slate-600 border border-slate-200' :
            'bg-blue-50 text-blue-600 border border-blue-200'
          }`}>
            Gallery
          </span>
          <h2 className={`text-3xl md:text-4xl font-bold mt-4 mb-4 ${variantClasses.text}`}>
            {domainConfig?.gallery?.title || `See Our ${brand.name} Solutions`}
          </h2>
          <p className={`text-lg ${variantClasses.textSecondary}`}>
            {domainConfig?.gallery?.subtitle || 'Explore our comprehensive range of products and services designed for your fleet needs'}
          </p>
        </div>

        {/* Slider Container */}
        <div className="relative">
          {/* Main Slider */}
          <div className={`relative overflow-hidden rounded-2xl shadow-2xl ${
            variant === 'creative' ? 'transform rotate-1' : ''
          }`}>
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {images.map((image, index) => (
                <div key={index} className="w-full flex-shrink-0 relative">
                  <div className="w-full h-[300px] md:h-[600px] flex items-center justify-center bg-gray-100 overflow-hidden">
                    <img
                      ref={(el) => imageRefs.current[index] = el}
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        handleImageErrorWithFallback(e, brand.id, `gallery-${index + 1}`);
                      }}
                    />
                  </div>
                  
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className={`absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 ${
              variant === 'modern' ? 'bg-white/90 hover:bg-white text-gray-800' :
              variant === 'dark' ? 'bg-white/90 hover:bg-white text-gray-800' :
              'bg-white/90 hover:bg-white text-gray-800'
            }`}
            aria-label="Previous image"
          >
            <ChevronLeftIcon className="w-6 h-6" />
          </button>
          
          <button
            onClick={nextSlide}
            className={`absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full shadow-lg transition-all duration-300 hover:scale-110 ${
              variant === 'modern' ? 'bg-white/90 hover:bg-white text-gray-800' :
              variant === 'dark' ? 'bg-white/90 hover:bg-white text-gray-800' :
              'bg-white/90 hover:bg-white text-gray-800'
            }`}
            aria-label="Next image"
          >
            <ChevronRightIcon className="w-6 h-6" />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-8 space-x-2">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide 
                    ? (variant === 'modern' ? 'bg-purple-400 scale-125' :
                       variant === 'dark' ? 'bg-blue-400 scale-125' :
                       variant === 'creative' ? 'bg-orange-400 scale-125' :
                       variant === 'elegant' ? 'bg-slate-600 scale-125' :
                       'bg-blue-600 scale-125')
                    : (variant === 'modern' ? 'bg-white/30 hover:bg-white/50' :
                       variant === 'dark' ? 'bg-white/30 hover:bg-white/50' :
                       'bg-gray-300 hover:bg-gray-400')
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Auto-play Toggle */}
          <div className="flex justify-center mt-4">
            <button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                isAutoPlaying 
                  ? (variant === 'modern' ? 'bg-purple-500 text-white' :
                     variant === 'dark' ? 'bg-blue-500 text-white' :
                     variant === 'creative' ? 'bg-orange-500 text-white' :
                     variant === 'elegant' ? 'bg-slate-600 text-white' :
                     'bg-blue-600 text-white')
                  : (variant === 'modern' ? 'bg-white/20 text-white hover:bg-white/30' :
                     variant === 'dark' ? 'bg-white/20 text-white hover:bg-white/30' :
                     'bg-gray-200 text-gray-700 hover:bg-gray-300')
              }`}
            >
              {isAutoPlaying ? 'Pause' : 'Play'} Auto-slide
            </button>
          </div>
        </div>

        {/* Thumbnail Navigation */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`relative overflow-hidden rounded-lg transition-all duration-300 ${
                index === currentSlide 
                  ? (variant === 'modern' ? 'ring-4 ring-purple-400 scale-105' :
                     variant === 'dark' ? 'ring-4 ring-blue-400 scale-105' :
                     variant === 'creative' ? 'ring-4 ring-orange-400 scale-105' :
                     variant === 'elegant' ? 'ring-4 ring-slate-400 scale-105' :
                     'ring-4 ring-blue-600 scale-105')
                  : 'hover:scale-105'
              }`}
            >
              <div className="w-full h-32 flex items-center justify-center bg-gray-100 overflow-hidden">
                <img
                  ref={(el) => imageRefs.current[index + 10] = el}
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    handleImageErrorWithFallback(e, brand.id, `gallery-${index + 1}`);
                  }}
                />
              </div>
              {index === currentSlide && (
                <div className={`absolute inset-0 flex items-center justify-center ${
                  variant === 'modern' ? 'bg-purple-400/20' :
                  variant === 'dark' ? 'bg-blue-400/20' :
                  variant === 'creative' ? 'bg-orange-400/20' :
                  variant === 'elegant' ? 'bg-slate-400/20' :
                  'bg-blue-600/20'
                }`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    variant === 'modern' ? 'bg-purple-400' :
                    variant === 'dark' ? 'bg-blue-400' :
                    variant === 'creative' ? 'bg-orange-400' :
                    variant === 'elegant' ? 'bg-slate-600' :
                    'bg-blue-600'
                  }`}>
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VariantImageSlider;
